import { useRef } from 'react';
import { useTranslation } from 'react-i18next';

import type { CardTemplateDto } from '../../../api/cards';

export function TemplateStep({
  savedTemplates,
  selectedTemplateId,
  resolvedSvg,
  activeTpl,
  customTemplateFile,
  onSelect,
  onPickFile,
  onClear,
}: {
  savedTemplates: CardTemplateDto[];
  selectedTemplateId: string;
  resolvedSvg: string | null;
  activeTpl: CardTemplateDto | undefined;
  customTemplateFile: File | null;
  onSelect: (id: string) => Promise<void>;
  onPickFile: (file: File) => Promise<void>;
  onClear: () => void;
}) {
  const { t } = useTranslation();
  const fileRef = useRef<HTMLInputElement>(null);
  const activeTplBack = activeTpl?.backSide ?? null;

  if (resolvedSvg) {
    return (
      <div className="space-y-1 border border-blue bg-blue/4 px-3 py-2">
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium text-blue">
            {activeTpl?.name ?? customTemplateFile?.name}
          </span>
          <button type="button" onClick={onClear} className="text-xs text-blue">
            {t('singleCardGenerator.change')}
          </button>
        </div>
        {activeTplBack && (
          <p className="text-xs text-magenta">+ verso : {activeTplBack.name}</p>
        )}
      </div>
    );
  }

  return (
    <>
      {savedTemplates.length > 0 && (
        <select
          aria-label={t('cardGenerator.templateLabel')}
          value={selectedTemplateId}
          onChange={(e) => void onSelect(e.target.value)}
          className="w-full px-3 py-2 text-sm"
        >
          <option value="">{t('singleCardGenerator.selectTemplate')}</option>
          {savedTemplates.map((tpl) => (
            <option key={tpl.id} value={tpl.id}>
              {tpl.name}
              {tpl.description ? ` — ${tpl.description}` : ''}
            </option>
          ))}
        </select>
      )}

      {!selectedTemplateId && (
        <>
          <div
            role="button"
            tabIndex={0}
            aria-label={t('photoUpload.dropZoneLabel')}
            onClick={() => fileRef.current?.click()}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                fileRef.current?.click();
              }
            }}
            className="cursor-pointer border border-dashed border-rule bg-bg-soft p-3 text-center transition-colors"
          >
            <p className="text-sm text-muted">
              {savedTemplates.length > 0
                ? t('singleCardGenerator.uploadCustom')
                : t('singleCardGenerator.uploadLabel')}
            </p>
          </div>
          <input
            ref={fileRef}
            type="file"
            accept=".svg,image/svg+xml"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) {
                void onPickFile(f);
              }
            }}
          />
        </>
      )}
    </>
  );
}
